<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: 'Buscar'
    },
    tooltip: {
        type: String,
    }
});

const emit = defineEmits(['update:modelValue', 'buscar']);

const hoverBusca = ref(false);
const campoInput = ref(null);

const searchValue = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
});

// Reaproveita os listeners de "@keyup.enter" já usados pelas listagens,
// disparando o mesmo evento nativo a partir do input.
const dispararBusca = () => {
    const input = campoInput.value?.$el;

    if (input) {
        input.focus();
        input.dispatchEvent(new KeyboardEvent('keyup', {
            key: 'Enter',
            code: 'Enter',
            bubbles: true
        }));
    }

    emit('buscar', searchValue.value);
};
</script>

<template>
    <TooltipElemento v-if="tooltip" :text="tooltip" position="bottom" class="campo-busca-tooltip"
        title="Opções de Busca">
        <IconField iconPosition="left" class="w-full">
            <InputIcon class="pi pi-search text-sm" style="margin-top: -7px;"></InputIcon>
            <InputText ref="campoInput" v-model="searchValue" :placeholder="placeholder" class="w-full"
                style="padding: 0.57rem 3.6rem 0.57rem 2.2rem; font-size: 13px;" />

            <Transition name="campo-busca-surgir">
                <span v-if="searchValue"
                    class="campo-busca-atalho border-1 surface-border border-round p-1 cursor-pointer select-none"
                    :class="hoverBusca ? 'bg-gray-200 text-800 font-medium' : 'surface-100 text-500'"
                    @mouseenter="hoverBusca = true" @mouseleave="hoverBusca = false" @click="dispararBusca">
                    <span class="campo-busca-atalho-textos" :class="{ 'campo-busca-atalho-textos--ativo': hoverBusca }">
                        <span class="campo-busca-atalho-texto" aria-label="Enter">
                            <svg class="campo-busca-atalho-icone" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <path d="M20 5v6a3 3 0 0 1-3 3H5" />
                                <path d="m9 10-4 4 4 4" />
                            </svg>
                            Enter
                        </span>
                        <span class="campo-busca-atalho-texto">Buscar</span>
                    </span>
                </span>
            </Transition>
        </IconField>
    </TooltipElemento>

    <IconField v-else iconPosition="left" class="w-full">
        <InputIcon class="pi pi-search text-sm" style="margin-top: -7px;"></InputIcon>
        <InputText ref="campoInput" v-model="searchValue" :placeholder="placeholder" class="w-full"
            style="padding: 0.57rem 3.6rem 0.57rem 2.2rem; font-size: 13px;" />

        <Transition name="campo-busca-surgir">
            <span v-if="searchValue"
                class="campo-busca-atalho border-1 surface-border border-round p-1 cursor-pointer select-none"
                :class="hoverBusca ? 'bg-gray-200 text-800 font-medium' : 'surface-100 text-500'"
                @mouseenter="hoverBusca = true" @mouseleave="hoverBusca = false" @click="dispararBusca">
                <span class="campo-busca-atalho-textos" :class="{ 'campo-busca-atalho-textos--ativo': hoverBusca }">
                    <span class="campo-busca-atalho-texto" aria-label="Enter">
                        <svg class="campo-busca-atalho-icone" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M20 5v6a3 3 0 0 1-3 3H5" />
                            <path d="m9 10-4 4 4 4" />
                        </svg>
                        Enter
                    </span>
                    <span class="campo-busca-atalho-texto">Buscar</span>
                </span>
            </span>
        </Transition>
    </IconField>
</template>

<style>
.campo-busca-tooltip.tooltip-elemento-wrapper {
    display: flex !important;
    width: 100%;
}

.campo-busca-atalho {
    position: absolute;
    right: 8px;
    top: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    line-height: 1;
    white-space: nowrap;
    transform: translateY(-50%);
    transition: background-color 200ms ease, border-color 200ms ease, color 200ms ease, box-shadow 200ms ease;
}

.campo-busca-atalho:active {
    transform: translateY(-50%) scale(0.94);
    transition: transform 90ms ease;
}

/* Os rótulos ocupam a mesma célula do grid: a largura fica fixa na do maior e a
   troca acontece com um giro 3D, um rótulo virando para dar lugar ao outro. */
.campo-busca-atalho-textos {
    display: grid;
    align-items: center;
    justify-items: center;
    perspective: 4rem;
}

.campo-busca-atalho-texto {
    grid-area: 1 / 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25em;
    line-height: 1.2;
    white-space: nowrap;
    opacity: 1;
    transform: rotateX(0deg) translateY(0);
    backface-visibility: hidden;
    transition: transform 320ms cubic-bezier(0.2, 0.8, 0.3, 1), opacity 200ms ease;
}

.campo-busca-atalho-icone {
    display: block;
    width: 1.15em;
    height: 1.15em;
}

.campo-busca-atalho-textos--ativo .campo-busca-atalho-texto:first-child {
    opacity: 0;
    transform: rotateX(-90deg) translateY(-0.3em);
}

.campo-busca-atalho-textos:not(.campo-busca-atalho-textos--ativo) .campo-busca-atalho-texto:last-child {
    opacity: 0;
    transform: rotateX(90deg) translateY(0.3em);
}

/* Entrada com "pop" leve vindo da direita; saída mais curta e discreta. */
.campo-busca-surgir-enter-active {
    transition: opacity 160ms ease-out, transform 320ms cubic-bezier(0.34, 1.4, 0.64, 1);
}

.campo-busca-surgir-leave-active {
    transition: opacity 130ms ease-in, transform 180ms cubic-bezier(0.4, 0, 1, 1);
}

.campo-busca-surgir-enter-from {
    opacity: 0;
    transform: translateY(-50%) translateX(0.5rem) scale(0.7);
}

.campo-busca-surgir-leave-to {
    opacity: 0;
    transform: translateY(-50%) translateX(0.25rem) scale(0.85);
}

@media (prefers-reduced-motion: reduce) {

    .campo-busca-atalho,
    .campo-busca-atalho-texto,
    .campo-busca-surgir-enter-active,
    .campo-busca-surgir-leave-active {
        transition-duration: 1ms;
    }
}
</style>
